/**
 * Base API Client
 * 
 * Provides a typed fetch wrapper with error handling for all API requests.
 * Implements APIException and NetworkException for structured error handling.
 */

export interface APIError {
  error: string;
  message: string;
  next_action?: string;
  request_id?: string;
  quota?: Record<string, unknown>;
  technical_reason?: string;
  status_code: number;
}

export type AuthTokenProvider = () => Promise<string | null> | string | null;

let authTokenProvider: AuthTokenProvider | null = null;

export function setAuthTokenProvider(provider: AuthTokenProvider): void {
  authTokenProvider = provider;
}

export function clearAuthTokenProvider(): void {
  authTokenProvider = null;
}

export async function getAuthToken(): Promise<string | null> {
  if (!authTokenProvider) return null;
  return authTokenProvider();
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Exception thrown when API returns an error response
 */
export class APIException extends Error {
  constructor(public error: APIError) {
    super(error.message);
    this.name = 'APIException';
  }
}

/**
 * Exception thrown when network request fails
 */
export class NetworkException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkException';
  }
}

/**
 * Base API client with typed request methods
 */
export class APIClient {
  private baseURL: string;

  constructor(baseURL: string = '') {
    this.baseURL = baseURL;
  }

  /**
   * Generic request method with error handling
   */
  async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const authHeaders = await getAuthHeaders();
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...options?.headers,
        },
      });

      if (!response.ok) {
        const body = await response.json();
        const error: APIError = body.detail || body;
        throw new APIException(error);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIException) {
        throw error;
      }
      throw new NetworkException('Network request failed');
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Export singleton instance with base URL from environment
export const apiClient = new APIClient(import.meta.env.VITE_API_BASE_URL || '');
